import * as THREE from 'three'
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js'
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js'

// Cache fonts to avoid reloading
const fontCache = new Map();

function loadFont(fontPath) {
  return new Promise((resolve, reject) => {
    if (fontCache.has(fontPath)) {
      resolve(fontCache.get(fontPath));
      return;
    }

    const loader = new FontLoader();
    loader.load(fontPath, (font) => {
      fontCache.set(fontPath, font);
      resolve(font);
    }, undefined, (error) => {
      reject(error);
    });
  });
}

// Cache text geometries to avoid recreating
const textGeometryCache = new Map();

function createTextGeometry(font, text, options = {}) {
  const cacheKey = `${text}-${JSON.stringify(options)}`;
  
  if (textGeometryCache.has(cacheKey)) {
    return textGeometryCache.get(cacheKey).clone();
  }

  const defaultOptions = {
    size: 80,
    depth: 5,
    curveSegments: 12,
    bevelEnabled: true,
    bevelThickness: 1,
    bevelSize: 8,
    bevelOffset: 0,
    bevelSegments: 5,
    ...options
  };

  const geometry = new TextGeometry(text, {
    font: font,
    ...defaultOptions
  });

  textGeometryCache.set(cacheKey, geometry);
  return geometry.clone();
}

export async function create_RGB_axes_helper_with_symbols(
  width_x,
  height_y,
  depth_z,
  group_of_cargo_area_attribute,
  options = {}
) {
  const {
    fontPaths = {
      x: './helvetiker_regular.typeface.json',
      y: './optimer_regular.typeface.json',
      z: './optimer_regular.typeface.json'
    },
    scaleFactor = 0.01,
    lineWidth = 2,
    opacity = 1.0
  } = options;

  // Convert and validate dimensions
  const width = Math.max(Number(width_x) || 0, 0.1);
  const height = Math.max(Number(height_y) || 0, 0.1);
  const depth = Math.max(Number(depth_z) || 0, 0.1);

  const min = Math.min(width, height, depth);
  const leg_min = min * 1.4;
  const leg = leg_min - min;

  try {
    // Load all fonts in parallel
    const [fontX, fontY, fontZ] = await Promise.all([
      loadFont(fontPaths.x),
      loadFont(fontPaths.y),
      loadFont(fontPaths.z)
    ]);

    // Create axes in parallel
    await Promise.all([
      createXAxis(width, height, depth, leg, min, scaleFactor, fontX, group_of_cargo_area_attribute, lineWidth, opacity),
      createYAxis(width, height, depth, leg, min, scaleFactor, fontY, group_of_cargo_area_attribute, lineWidth, opacity),
      createZAxis(width, height, depth, leg, min, scaleFactor, fontZ, group_of_cargo_area_attribute, lineWidth, opacity)
    ]);

  } catch (error) {
    console.error('Failed to create axes helper:', error);
    // Fallback: create simple axes without text
    createSimpleAxes(width, height, depth, leg, group_of_cargo_area_attribute, lineWidth);
  }
}

function createXAxis(width, height, depth, leg, min, scaleFactor, font, group, lineWidth, opacity) {
  // Create X text
  const textGeometry = createTextGeometry(font, 'X', { size: 80 });
  const textMaterial = new THREE.MeshBasicMaterial({
    color: 0xff0000,
    transparent: opacity < 1.0,
    opacity: opacity
  });
  
  const text = new THREE.Mesh(textGeometry, textMaterial);
  const textScale = scaleFactor * (min / 8);
  text.scale.set(textScale, textScale, 0.001);
  text.position.set(leg + width, 0, 0);
  group.add(text);

  // Create X line
  const lineMaterial = new THREE.LineBasicMaterial({ 
    color: 0xff0000,
    linewidth: lineWidth
  });
  const points = [
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3((leg + width) * 0.95, 0, 0)
  ];
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const line = new THREE.Line(geometry, lineMaterial);
  group.add(line);

  return { text, line };
}

function createYAxis(width, height, depth, leg, min, scaleFactor, font, group, lineWidth, opacity) {
  // Create Y text
  const textGeometry = createTextGeometry(font, 'Y', { size: 80 });
  const textMaterial = new THREE.MeshBasicMaterial({
    color: 0x00ff00,
    transparent: opacity < 1.0,
    opacity: opacity
  });
  
  const text = new THREE.Mesh(textGeometry, textMaterial);
  const textScale = scaleFactor * (min / 8);
  text.scale.set(textScale, textScale, 0.001);
  text.position.set(0, leg + height, 0);
  group.add(text);

  // Create Y line
  const lineMaterial = new THREE.LineBasicMaterial({ 
    color: 0x00ff00,
    linewidth: lineWidth
  });
  const points = [
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(0, (leg + height) * 0.95, 0)
  ];
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const line = new THREE.Line(geometry, lineMaterial);
  group.add(line);

  return { text, line };
}

function createZAxis(width, height, depth, leg, min, scaleFactor, font, group, lineWidth, opacity) {
  // Create Z text
  const textGeometry = createTextGeometry(font, 'Z', { size: 80 });
  const textMaterial = new THREE.MeshBasicMaterial({
    color: 0x0000ff,
    transparent: opacity < 1.0,
    opacity: opacity
  });
  
  const text = new THREE.Mesh(textGeometry, textMaterial);
  const textScale = scaleFactor * (min / 8);
  text.scale.set(textScale, textScale, 0.001);
  text.position.set(0, 0, leg + depth);
  text.rotation.y = Math.PI / 2;
  group.add(text);

  // Create Z line
  const lineMaterial = new THREE.LineBasicMaterial({ 
    color: 0x0000ff,
    linewidth: lineWidth
  });
  const points = [
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(0, 0, (leg + depth) * 0.95)
  ];
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const line = new THREE.Line(geometry, lineMaterial);
  group.add(line);

  return { text, line };
}

// Fallback function if font loading fails
function createSimpleAxes(width, height, depth, leg, group, lineWidth) {
  const axesHelper = new THREE.AxesHelper(Math.max(width, height, depth) * 1.2);
  group.add(axesHelper);
}

function createAxisWithArrow(direction, color, length, group, lineWidth = 2) {
  // Create line
  const lineMaterial = new THREE.LineBasicMaterial({ color, linewidth: lineWidth });
  const points = [new THREE.Vector3(0, 0, 0), new THREE.Vector3(...direction.map(v => v * length * 0.9))];
  const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
  const line = new THREE.Line(lineGeometry, lineMaterial);
  group.add(line);

  // Create arrowhead
  const arrowheadGeometry = new THREE.ConeGeometry(length * 0.05, length * 0.1, 8);
  const arrowheadMaterial = new THREE.MeshBasicMaterial({ color });
  const arrowhead = new THREE.Mesh(arrowheadGeometry, arrowheadMaterial);
  arrowhead.position.set(...direction.map(v => v * length * 0.95));
  
  // Rotate arrowhead to point in the right direction
  if (direction[0] !== 0) arrowhead.rotation.z = -Math.PI / 2;
  if (direction[2] !== 0) arrowhead.rotation.x = Math.PI / 2;
  
  group.add(arrowhead);

  return { line, arrowhead };
}